import React from "react";
import { Box, Divider, Grid, Link, List, ListItem, ListItemSecondaryAction, ListItemText, Paper, Skeleton } from '@mui/material';
import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { withRouter } from "react-router";
import { getSiteBillingPortal, getSitePaymentReport, getSiteSubscription } from "../../../actions/internal/SiteActions";
import StyledSubHeader from "../../common/StyledSubHeader";
import { getSiteBillingPortalById, getSiteById, getSiteInvoiceReportById, getSiteSubscriptionById } from "../../../selectors/SiteSelectors";
import StyledProgressCircle, { StyledLoadingSection } from "../../common/StyledProgressCircle";
import StyledChip from "../../common/StyledChip";
import { SUMMARY_TITLE_FONT } from "../../../constants/Styles";
import StyledLineItem from "../../common/StyledLineItem";
import { ContainedButton, TooltipIconButton } from "../../common/StyledButtons";
import Colors from "../../../constants/Colors";
import { AssignmentOutlined, ReceiptOutlined } from "@mui/icons-material";
import { getUserId } from "../../../selectors/UserSelectors";
import { formatDateToFancyString } from "../../../utilities/DateUtilities";
import StyledPage from "../../common/StyledPage";
import StyledSection from "../../common/StyledSection";

const ViewSiteBillingComponent = ({ match, history }) => {

    const [ isLoading, setIsLoading ] = useState(true);
    const [showAll, setShowAll] = useState(false);


    const siteId = match.params.site_id
    const invoiceReport = getSiteInvoiceReportById(siteId)
    const subscription = getSiteSubscriptionById(siteId);
    const billingUrl = getSiteBillingPortalById(siteId);
    const site = getSiteById(siteId);
  
    useEffect(() => {
      loadData().then();
    }, [])
  
    const loadData = async () => {
      try {
        const userId = getUserId()
        await Promise.all([
            getSitePaymentReport(siteId),
            getSiteSubscription(userId, siteId),
            getSiteBillingPortal(userId, siteId)
        ])
      } catch (ex) {
        console.error(ex);
      } finally {
        setIsLoading(false);
      }
    }

    const showLinkInNewTab = (url) => {
        const win = window.open(url, '_blank');
        win.focus();
    }

    const renderLoadingContent = () => (
        <StyledLoadingSection />
    )

    const humanizeStripeStatus = (status) => {
        return status
          .replace(/_/g, ' ')                // Replace underscores with spaces
          .split(' ')                        // Split into words
          .map(word => word.charAt(0).toUpperCase() + word.slice(1)) // Capitalize each word
          .join(' ');                        // Join back into a string
      }

    const handleShowMore = () => setShowAll(true);

    const renderPackageDetailsSection = () => {
        let total = 0;
        if(subscription 
            && subscription.stripe_subscription 
            && subscription.stripe_subscription.status === 'active') {
            total = subscription.stripe_subscription.items.data.reduce((acc, val) => acc + val.price.unit_amount*val.quantity, 0)
        }
        return (
            <StyledSection
                title="Service Pricing"
                rightAdornment={<StyledChip
                    text={subscription.stripe_subscription && subscription.stripe_subscription.collection_method === "charge_automatically" ? "Autopay On" : "Autopay Off"}
                    sx={{ backgroundColor: subscription.stripe_subscription && subscription.stripe_subscription.collection_method === "charge_automatically" ? Colors.primaryColor : Colors.greyColor }}
                />}
            >
                {!subscription || !subscription.stripe_subscription || subscription.stripe_subscription.status === 'canceled' ?
                        <>
                            <h4 style={{ margin: 0, fontSize: '24px' }}>No Active Services...</h4>
                            <p>It looks like you haven't choosen any services for your account yet. Our services offer a valuable way to make your mark on the web. If you're interested in getting started, contact us to find the perfect fit for you!</p>
                            <ContainedButton
                                    onClick={() => showLinkInNewTab(`https://consultwithkyle.com/appointments/quick-call`)}
                                >
                                Schedule A Call
                            </ContainedButton>
                        </>
                        :
                        <>
                            <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '25px'}}>
                                <StyledLineItem primary="Status" secondary={humanizeStripeStatus(subscription.stripe_subscription.status)} sx={{ flexDirection: 'column' }} primarySx={{ fontSize: '20px' }}/>
                                <StyledLineItem primary="Renewal Date" secondary={formatDateToFancyString(subscription.stripe_subscription.current_period_end * 1000)} sx={{ flexDirection: 'column' }} primarySx={{ fontSize: '20px' }}/>
                                <StyledLineItem primary="Hourly Rate" secondary={`$${(site.site_service_modules.consulting.general_consulting.pricing.default / 100).toFixed(2)}/hour`} sx={{ flexDirection: 'column' }} primarySx={{ fontSize: '20px' }}/>
                                <div></div>
                            </Box>
                            <Box>
                                <div style={{ ...SUMMARY_TITLE_FONT, fontSize: '20px', marginTop: '20px' }}>Item Breakdown</div>
                                {subscription.stripe_subscription.items.data.map((item, index) => (
                                    <div>
                                        {index !== 0 && <Divider /> }
                                        <StyledLineItem sx={{ marginTop: '10px', marginBottom: '10px' }} primary={`${item.product.name} (x${item.quantity})`} secondary={`$${((item.price.unit_amount*item.quantity) / 100).toFixed(2)} `}/>
                                    </div>
                                ))}
                                <Divider sx={{ borderBottomWidth: 'medium' }} />
                                <StyledLineItem sx={{ marginTop: '10px', marginBottom: '10px' }} primary="Total" secondary={`$${(total/100).toFixed(2)}`}/>
                            </Box>
                        </>
                    }
            </StyledSection>
        )
    }

    const renderDueInvoicesSection = () => (
        <StyledSection
            title="Due Invoices"
        >
            <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                    <List style={{ flex: 1 }}>
                        {invoiceReport && invoiceReport.report && invoiceReport.report.open_invoices && invoiceReport.report.open_invoices.invoices.map((invoice, index) => (
                            <ListItem
                                dense
                                sx={{
                                    backgroundColor: '#fff',
                                    marginBottom: '5px',
                                    borderBottom: index !== invoiceReport.report.open_invoices.invoices.length - 1 ? '1px solid #e0e0e0' : 'none',
                                }}
                                key={invoice.id}>
                                <ListItemText primary={<span style={{fontSize: 18}}>{invoice.name}</span>} secondary={`$${(invoice.amount_due/100).toFixed(2)}`} />
                                <ListItemSecondaryAction>
                                    {invoice.project_id && <TooltipIconButton
                                        text={'View Project Details'}
                                        icon= {<AssignmentOutlined sx={{ color: Colors.primaryColor }}
                                            onClick={() => history.push(`/dashboard/site/${siteId}/projects/${invoice.project_id}`)}
                                        /> } 
                                    />}
                                    <TooltipIconButton
                                        text={'View Invoice & Pay Now'}
                                        icon= {<ReceiptOutlined sx={{ color: Colors.primaryColor }}
                                            onClick={() => showLinkInNewTab(invoice.hosted_invoice_url)}
                                        />}
                                    />
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                        {(!invoiceReport || !invoiceReport.report || !invoiceReport.report.open_invoices || !invoiceReport.report.open_invoices.invoices || invoiceReport.report.open_invoices.invoices.length === 0) &&
                            <p>Woo-hoo! No invoices are due <span role="img" aria-label="party face emoji">🥳</span></p>
                        }
                    </List>
                </Paper>
        </StyledSection>
    )

    const renderInvoiceHistorySection = () => {
        const invoices = invoiceReport.report.paid_invoices.invoices || [];
        const visibleInvoices = showAll ? invoices : invoices.slice(0, 5);

        return (
        <StyledSection title="Invoice History">
            <Paper variant='outlined' sx={{ marginTop: '5px', paddingLeft: '10px', paddingRight: '10px' }}>
                <List style={{ flex: 1 }}>
                    {visibleInvoices.map((invoice, index) => (
                        <ListItem
                            dense
                            sx={{
                                backgroundColor: '#fff',
                                marginBottom: '5px',
                                borderBottom: index !== visibleInvoices.length - 1 ? '1px solid #e0e0e0' : 'none',
                            }}
                            key={invoice.id}
                        >
                            <ListItemText primary={invoice.name} secondary={`$${(invoice.amount_due / 100).toFixed(2)}`} />
                            <ListItemSecondaryAction>
                                {invoice.project_id && (
                                    <TooltipIconButton
                                        text={'View Project Details'}
                                        icon={
                                            <AssignmentOutlined
                                                sx={{ color: Colors.primaryColor }}
                                                onClick={() => history.push(`/dashboard/site/${siteId}/projects/${invoice.project_id}`)}
                                            />
                                        }
                                    />
                                )}
                                <TooltipIconButton
                                    text={'View Invoice'}
                                    icon={
                                        <ReceiptOutlined
                                            sx={{ color: Colors.primaryColor }}
                                            onClick={() => showLinkInNewTab(invoice.hosted_invoice_url)}
                                        />
                                    }
                                />
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                    {invoices.length === 0 && <p>No past invoices.</p>}
                </List>

            </Paper>

            {invoices.length > 5 && !showAll ? (
                <p style={{ cursor: 'pointer', color: Colors.primaryColor, textAlign: 'center' }} onClick={handleShowMore}>
                    Show More
                </p>)
                :
                <p style={{ textAlign: 'center' }}>If you're looking for an older invoice or can't find the one you need, please <Link href="https://consultwithkyle.com/contact-us" target="_blank">let us know</Link>!</p>
            }
            
        </StyledSection>
        )
    }

    const renderBillingPreferencesSection = () => (
        <StyledSection
        title="Update Billing Preferences"
        >
            <p>Looking to update your payment method or financial contact information? Click the button below to be brought to the Billing Portal powered by Stripe.</p>
            {billingUrl ? <ContainedButton onClick={() =>  window.open(billingUrl.url)} sx={{ marginTop: '20px' }}>Update Billing Preferences</ContainedButton> : <Skeleton height={50} width={200} />}
        </StyledSection>
    )

    return (
    <StyledPage>
        <Grid container spacing={2}>
            <Grid item xs={12}>
            <StyledSubHeader title="Billing" />
            </Grid>
            <Grid item xs={12}>
            {isLoading ? renderLoadingContent() :
                <>
                    {renderPackageDetailsSection()}
                    {renderDueInvoicesSection()}
                    {renderInvoiceHistorySection()}
                    {renderBillingPreferencesSection()}
                </>
            
            }
           </Grid>
        </Grid>
    </StyledPage>
    )
}

export default observer(withRouter(ViewSiteBillingComponent));
